import { FC, useMemo } from "react";
import { Drawer } from "antd";
import { Link } from "react-router-dom";
import { Typography } from "../../../design-system";
import { DocumentCard } from "../../../design-system";
import {
    AnalysisType,
    IntakeMode,
    Project,
    ProjectAnalysis,
    ProjectAnalysisInput,
    ProjectSourceType,
    RfxProjectSource,
    getAnalysisLabel,
} from "../../../types/ProjectTypes";
import styles from "./ProjectTimeline.module.less";

const { Text } = Typography;

interface TimelineEvent {
    type: "intake" | "update" | "completed";
    label: string;
    date: Date;
    author: string;
    input?: ProjectAnalysisInput;
}

function formatDateTime(date: Date): string {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatDayKey(date: Date): string {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDayLabel(date: Date): string {
    return new Date(date).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

function resolveChoice(value: any, choices?: any[]): string {
    if (!choices) return String(value);
    const choice = choices.find((c: any) => (typeof c === "object" ? c.value : c) === value);
    if (typeof choice === "object" && choice.text) return choice.text;
    return String(value);
}

// SurveyJS stores "other" as the answer value and puts the user's typed text
// in a separate `<questionName>-Comment` response key; show the typed text.
function resolveAnswer(value: any, element: any, otherComment?: string): string | null {
    if (value == null) return null;
    if (Array.isArray(value)) {
        const labels = value.map((v: any) =>
            v === "other" && otherComment ? otherComment : resolveChoice(v, element.choices)
        );
        const text = labels.filter(Boolean).join(", ");
        return text || null;
    }
    if (typeof value === "string" && !value.trim()) return null;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === "other" && otherComment) return otherComment;
    if (element.choices) return resolveChoice(value, element.choices);
    return String(value);
}

interface QAItem {
    question?: string;
    answer: string;
}

function extractQA(survey: { schema: object; response: object }): QAItem[] {
    const schema = survey.schema as any;
    const response = survey.response as any;
    if (!schema?.pages || !response) return [];

    const qa: QAItem[] = [];
    for (const page of schema.pages) {
        for (const element of page.elements || []) {
            const value = response[element.name];
            const rawComment = response[`${element.name}-Comment`];
            const otherComment =
                typeof rawComment === "string" && rawComment.trim() ? rawComment.trim() : undefined;
            const answer = resolveAnswer(value, element, otherComment);
            if (!answer) continue;
            qa.push({ question: element.title || undefined, answer });
        }
    }
    return qa;
}

function buildEvents(analyses: ProjectAnalysis[]): TimelineEvent[] {
    const events: TimelineEvent[] = [];
    const sorted = [...analyses].sort((a, b) => a.version - b.version);

    for (const analysis of sorted) {
        const closing =
            analysis.type === AnalysisType.ROI || analysis.type === AnalysisType.COMPLETE;

        if (closing) {
            // Closing analysis: show as an input event with the closing label
            const lastInput = analysis.inputs?.[analysis.inputs.length - 1];
            if (lastInput) {
                const creator = lastInput.creator;
                events.push({
                    type: "update",
                    label: getAnalysisLabel(analysis),
                    date: new Date(lastInput.recordCreationDate),
                    author: `${creator.firstName} ${creator.lastName}`,
                    input: lastInput,
                });
            }
            // A signed deal's savings report is delivered later, like a playbook.
            if (analysis.type === AnalysisType.ROI && analysis.completedDate) {
                events.push({
                    type: "completed",
                    label: `${getAnalysisLabel(analysis)} delivered`,
                    date: new Date(analysis.completedDate),
                    author: "Infinity Loop",
                });
            }
        } else {
            // Regular analysis: show each input revision
            if (analysis.inputs) {
                for (let i = 0; i < analysis.inputs.length; i++) {
                    const input = analysis.inputs[i];
                    const isIntake = analysis.version === 1 && i === 0;
                    const creator = input.creator;
                    events.push({
                        type: isIntake ? "intake" : "update",
                        label: isIntake ? "Intake" : "Update",
                        date: new Date(input.recordCreationDate),
                        author: `${creator.firstName} ${creator.lastName}`,
                        input,
                    });
                }
            }

            // Playbook delivery event
            if (analysis.completedDate) {
                events.push({
                    type: "completed",
                    label: `${getAnalysisLabel(analysis)} delivered`,
                    date: new Date(analysis.completedDate),
                    author: "Infinity Loop",
                });
            }
        }
    }

    events.sort((a, b) => b.date.getTime() - a.date.getTime());
    return events;
}

interface ProjectTimelineProps {
    project: Project;
    open: boolean;
    onClose: () => void;
}

export const ProjectTimeline: FC<ProjectTimelineProps> = ({ project, open, onClose }) => {
    const events = useMemo(() => buildEvents(project.analyses), [project.analyses]);
    const source = project.source;

    const grouped = useMemo(() => {
        const groups: Array<{ key: string; label: string; events: TimelineEvent[] }> = [];
        for (const event of events) {
            const key = formatDayKey(event.date);
            const existing = groups.find((g) => g.key === key);
            if (existing) {
                existing.events.push(event);
            } else {
                groups.push({
                    key,
                    label: formatDayLabel(event.date),
                    events: [event],
                });
            }
        }
        return groups;
    }, [events]);

    return (
        <Drawer
            title="Project Timeline"
            placement="right"
            width={624}
            open={open}
            onClose={onClose}
        >
            <div className={styles.timeline}>
                {grouped.map((group) => (
                    <div key={group.key} className={styles.dayGroup}>
                        <div className={styles.dayHeader}>{group.label}</div>
                        <div className={styles.dayEvents}>
                            {group.events.map((event, i) => (
                                <div key={i} className={styles.event}>
                                    <div className={styles.dotColumn}>
                                        <div
                                            className={`${styles.dot} ${
                                                event.type === "completed"
                                                    ? styles.dotCompleted
                                                    : ""
                                            }`}
                                        />
                                    </div>
                                    <div className={styles.eventContent}>
                                        <div className={styles.eventLabel}>{event.label}</div>
                                        <Text type="secondary" className={styles.eventMeta}>
                                            {event.author} &middot; {formatDateTime(event.date)}
                                        </Text>
                                        {event.type === "intake" &&
                                            source?.type === ProjectSourceType.RFX && (
                                                <RfxSourceLine source={source} />
                                            )}
                                        <EventBody event={event} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                {events.length === 0 && <Text type="secondary">No activity yet.</Text>}
            </div>
        </Drawer>
    );
};

const RfxSourceLine: FC<{ source: RfxProjectSource }> = ({ source }) => (
    <Text type="secondary" className={styles.eventMeta}>
        Sourced from RFx: <Link to={`/project/${source.projectId}`}>{source.projectName}</Link>
    </Text>
);

const EventBody: FC<{ event: TimelineEvent }> = ({ event }) => {
    if (!event.input) return null;
    const { input } = event;

    // For intake: show all documents. For updates: show only changed documents.
    const docsToShow =
        event.type === "intake" ? input.documents : input.documents.filter((d) => d.change);

    // Guided-intake inputs answered via the free-form chat show the transcript;
    // inputs answered via the guided questions survey — and plain survey
    // inputs — show the survey Q&A.
    const transcript =
        input.guidedIntake && input.intakeMode === IntakeMode.CHAT ? input.messages || [] : [];
    const qa = transcript.length === 0 && input.survey ? extractQA(input.survey) : [];

    if (docsToShow.length === 0 && qa.length === 0 && transcript.length === 0) return null;

    return (
        <div className={styles.eventBody}>
            {docsToShow.length > 0 && (
                <div className={styles.docSection}>
                    {docsToShow.map((doc) => (
                        <div
                            key={doc.id}
                            className={`${styles.docRow} ${doc.change === "added" ? styles.docRowAdded : doc.change === "removed" ? styles.docRowRemoved : ""}`}
                        >
                            <DocumentCard name={doc.document.name} />
                        </div>
                    ))}
                </div>
            )}
            {transcript.length > 0 && (
                <div className={styles.transcriptSection}>
                    {transcript.map((message) => (
                        <div key={message.id} className={styles.transcriptItem}>
                            <Text type="secondary" className={styles.transcriptRole}>
                                <strong>
                                    {message.role === "user" ? event.author : "Infinity Loop"}
                                </strong>
                            </Text>
                            <div className={styles.transcriptContent}>{message.content}</div>
                        </div>
                    ))}
                </div>
            )}
            {qa.length > 0 && (
                <div className={styles.qaSection}>
                    {qa.map((item, i) => (
                        <div key={i} className={styles.qaItem}>
                            {item.question ? (
                                <>
                                    <Text type="secondary" className={styles.qaQuestion}>
                                        <strong>{item.question}</strong>
                                    </Text>
                                    <div className={styles.qaAnswer}>{item.answer}</div>
                                </>
                            ) : (
                                <div className={styles.qaAnswer}>{item.answer}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
